/**
 * PrepayMethodFields.tsx
 *
 * How a membership prepayment is paid (plan 2026-10-22-001, R6): a card, saved
 * or entered new (it is saved to the patient, then charged), or a method the
 * clinic records, such as cash or check, with a reference number. Shared by
 * the sign-up (AddMembershipModal) and Pay Ahead (RecurringDetailModal), which
 * send the same `RecurringPrepayPayload`.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listPaymentMethodOptions,
  type BillingMethod,
  type PaymentMethodOption,
  type RecurringPrepayPayload,
} from '@/services/payments.service';
import {
  PaymentSourceSelector,
  parseManualToken,
  parseTerminalToken,
} from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';

/** The prepayment-method select's value for "a card"; any other value is a paymentMethodID. */
export const PREPAY_BY_CARD = 'card';

/** A saved card's token: not "new", a terminal handle or a manual method. */
export function isSavedCardToken(token: string): boolean {
  return token !== '' && token !== 'new'
    && parseTerminalToken(token) === null && parseManualToken(token) === null;
}

/** Why a card choice can't be charged yet, or null when it is a saved card. */
export function prepayCardProblem(token: string): string | null {
  if (token === 'new') return 'Finish entering the new card, or choose a saved card.';
  if (!isSavedCardToken(token)) return 'Choose the card that pays the prepayment.';
  return null;
}

/**
 * The methods a prepayment can be recorded with. Store-credit methods are left
 * out, as in PaymentSourceSelector: the prepayment only records the method's
 * name, so one would redeem no credit.
 */
export function useRecordedPrepayMethods(enabled: boolean): PaymentMethodOption[] {
  const { data } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => (data ?? []).filter((o) => o.active && !o.isCreditType), [data]);
}

/** The card a card prepayment charges. */
export interface PrepayCard {
  token: string;
  title?: string | null;
  paymentMethodID?: number | null;
}

/**
 * The prepayment to send, or an Error saying what is missing. The amount is
 * never sent: the server takes the monthly amount times the months.
 */
export function buildPrepayPayload(
  months: number,
  choice: string,
  card: PrepayCard,
  recordedMethods: PaymentMethodOption[],
  referenceNumber: string,
): RecurringPrepayPayload {
  if (choice === PREPAY_BY_CARD) {
    const problem = prepayCardProblem(card.token);
    if (problem) throw new Error(problem);
    return {
      months,
      method: card.title || 'Card',
      paymentMethodToken: card.token,
      paymentMethodID: card.paymentMethodID ?? undefined,
    };
  }

  const recorded = recordedMethods.find((o) => String(o.paymentMethodID) === choice);
  if (!recorded) throw new Error('Choose how the prepayment is paid.');
  return {
    months,
    method: recorded.name,
    paymentMethodID: recorded.paymentMethodID,
    referenceNumber: referenceNumber.trim() || undefined,
  };
}

/**
 * The "Prepayment method" select, then the card to charge or the reference
 * number to record. "New card" opens the card form; the card it saves becomes
 * the prepayment card. In-person terminals aren't offered: a prepayment is
 * charged to a saved card.
 */
export function PrepayMethodFields({
  patientId,
  merchantId,
  recordedMethods,
  choice,
  onChoiceChange,
  cardToken,
  onCardChange,
  allowNewCard = true,
  referenceNumber,
  onReferenceNumberChange,
}: {
  patientId: number;
  merchantId: number | null;
  recordedMethods: PaymentMethodOption[];
  /** PREPAY_BY_CARD or a recorded method's paymentMethodID. */
  choice: string;
  onChoiceChange: (choice: string) => void;
  cardToken: string;
  /** Keep it stable (useCallback): the new-card form remounts when it changes. */
  onCardChange: (token: string, method: BillingMethod | null) => void;
  /** False hides "New card", e.g. while another new-card form is open on the page. */
  allowNewCard?: boolean;
  referenceNumber: string;
  onReferenceNumberChange: (value: string) => void;
}) {
  const handleNewCardSaved = useCallback(
    (method: BillingMethod) => onCardChange(method.token, method),
    [onCardChange],
  );
  const byCard = choice === PREPAY_BY_CARD;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-1 text-sm text-slate-700">
          <span className="font-medium">Prepayment method</span>
          <select
            value={choice}
            onChange={(e) => onChoiceChange(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={PREPAY_BY_CARD}>Card</option>
            {recordedMethods.length > 0 ? (
              <optgroup label="Recorded methods">
                {recordedMethods.map((o) => (
                  <option key={o.paymentMethodID} value={String(o.paymentMethodID)}>{o.name}</option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        {byCard ? (
          <PaymentSourceSelector
            label="Prepayment card"
            patientId={patientId}
            value={cardToken}
            merchantId={merchantId}
            onChange={onCardChange}
            hideNewOption={!allowNewCard}
            hideTerminals
            placeholder={allowNewCard ? 'Choose a card' : 'Choose a saved card'}
          />
        ) : (
          <label className="space-y-1 text-sm text-slate-700">
            <span className="font-medium">Reference Number</span>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => onReferenceNumberChange(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
        )}
      </div>
      {byCard && allowNewCard ? (
        <ForwardBillingMethodCapture
          active={cardToken === 'new'}
          patientId={patientId}
          merchantId={merchantId}
          onSaved={handleNewCardSaved}
        />
      ) : null}
    </div>
  );
}
